// SSEConnectionManager.js
import storage from "../../utils/storage";

export class SSEConnectionManager {
  constructor() {
    // 创建广播频道
    this.broadcastChannel = new BroadcastChannel('sse_notifications');
    this.isMaster = false;
    this.eventSource = null;
    this.masterCheckInterval = null;
    
    // 初始化连接管理
    this.init();
  }

  init() {
    // 尝试成为主连接
    this.tryBecomeMaster();
    
    // 设置广播消息监听
    this.setupBroadcastListener();
    
    // 设置页面可见性监听
    this.setupVisibilityHandler();
    
    // 设置页面卸载清理
    this.setupUnloadHandler();
  }

  /**
   * 尝试成为主连接（只有主连接会创建真实SSE）
   */
  tryBecomeMaster() {
    const masterKey = 'sse_master_tab';
    const tabId = this.generateTabId();
    
    try {
      // 尝试声明主连接身份
      const currentMaster = localStorage.getItem(masterKey);
      
      if (!currentMaster || this.isMasterExpired(currentMaster)) {
        // 没有主连接或主连接已过期，尝试成为新的主连接
        const masterClaim = JSON.stringify({
          tabId: tabId,
          timestamp: Date.now()
        });
        
        localStorage.setItem(masterKey, masterClaim);
        
        // 短暂延迟后确认主连接状态
        setTimeout(() => {
          const confirmedMaster = localStorage.getItem(masterKey);
          if (confirmedMaster && JSON.parse(confirmedMaster).tabId === tabId) {
            this.isMaster = true;
            console.log('✅ 成为主连接标签页');
            this.startSSEConnection();
            this.startMasterHeartbeat();
            
            // 广播主连接声明
            this.broadcastChannel.postMessage({
              type: 'MASTER_ANNOUNCEMENT',
              tabId: tabId
            });
          } else {
            this.isMaster = false;
            console.log('❌ 主连接竞争失败');
          }
        }, 100);
      } else {
        this.isMaster = false;
        console.log('🔍 已有活跃主连接');
      }
    } catch (error) {
      console.error('主连接选举失败:', error);
      this.isMaster = false;
    }
  }

  /**
   * 启动真实的SSE连接（仅主连接调用）
   */
  startSSEConnection() {
    if (!this.isMaster) return;
    
    if(!storage.getItem("token")){
      console.log('未登录，请先登录！');
      return false;
    }

    try {
      let sseConnectUrl = `/api/sse/createSseConnect?token=${storage.getItem("token")}`
      this.eventSource = new EventSource(sseConnectUrl);
      
      this.eventSource.onopen = (event) => {
        console.log('🔗 SSE连接已建立');
        this.broadcastChannel.postMessage({
          type: 'SSE_CONNECTION_STATUS',
          status: 'connected'
        });
      };
      
      this.eventSource.onmessage = (event) => {
        if (event.lastEventId === "resultId") {
          console.log('Received SSE message:', event);
          const data = event.data; //JSON.parse(event.data);
          
          // 广播消息给所有标签页
          this.broadcastChannel.postMessage({
            type: 'SSE_MESSAGE',
            payload: data,
            timestamp: Date.now()
          });
          
          // 调用消息处理回调
          if (this.onMessageCallback) {
            this.onMessageCallback(data);
          }
        }
      };
      
      this.eventSource.onerror = (event) => {
        console.error('SSE连接错误:', event);
        this.broadcastChannel.postMessage({
          type: 'SSE_CONNECTION_STATUS',
          status: 'error'
        });
        
        // 错误时重连
        // setTimeout(() => {
        //   if (this.isMaster) {
        //     this.restartSSEConnection();
        //   }
        // }, 5000);
      };
      
    } catch (error) {
      console.error('创建SSE连接失败:', error);
    }
  }

  /**
   * 设置广播消息监听
   */
  setupBroadcastListener() {
    this.broadcastChannel.onmessage = (event) => {
      const message = event.data;
      
      switch (message.type) {
        case 'SSE_MESSAGE':
          // 处理SSE消息
          if (this.onMessageCallback) {
            this.onMessageCallback(message.payload);
          }
          break;
          
        case 'MASTER_ANNOUNCEMENT':
          // 收到其他标签页的主连接声明
          if (!this.isMaster) {
            this.clearMasterClaim();
          }
          break;
          
        case 'SSE_CONNECTION_STATUS':
          console.log(`SSE连接状态: ${message.status}`);
          break;
          
        case 'REQUEST_MASTER_STATUS':
          // 收到状态查询请求，如果是主连接则响应
          if (this.isMaster) {
            this.broadcastChannel.postMessage({
              type: 'MASTER_STATUS_RESPONSE',
              tabId: this.getTabId()
            });
          }
          break;
      }
    };
  }

  /**
   * 主连接心跳（保持主连接状态）
   */
  startMasterHeartbeat() {
    if (!this.isMaster) return;
    
    this.masterCheckInterval = setInterval(() => {
      if (this.isMaster) {
        const masterClaim = JSON.stringify({
          tabId: this.getTabId(),
          timestamp: Date.now()
        });
        localStorage.setItem('sse_master_tab', masterClaim);
      }
    }, 3000); // 每3秒更新一次
  }

  /**
   * 页面可见性处理
   */
  setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 页面隐藏时的处理
        this.handlePageHidden();
      } else {
        // 页面重新显示时的处理
        this.handlePageVisible();
      }
    });
  }

  handlePageHidden() {
    if (this.isMaster) {
      // 主连接页面隐藏时，可以暂停部分操作但不关闭连接
      console.log('主连接页面隐藏');
    }
  }

  handlePageVisible() {
    // 页面重新显示时检查主连接状态
    console.log('主连接页面显示');
    if (!this.isMaster) {
      this.checkMasterStatus();
    }
  }

  /**
   * 检查主连接状态
   */
  checkMasterStatus() {
    // 广播请求主连接状态
    this.broadcastChannel.postMessage({
      type: 'REQUEST_MASTER_STATUS'
    });
    
    // 设置超时，如果没有响应则认为没有主连接
    setTimeout(() => {
      const master = localStorage.getItem('sse_master_tab');
      if (!master || this.isMasterExpired(master)) {
        console.log('主连接可能已失效，尝试成为主连接');
        this.tryBecomeMaster();
      }
    }, 1000);
  }

  /**
   * 清理主连接声明
   */
  clearMasterClaim() {
    const master = localStorage.getItem('sse_master_tab');
    if (master) {
      const masterData = JSON.parse(master);
      if (masterData.tabId === this.getTabId()) {
        localStorage.removeItem('sse_master_tab');
      }
    }
  }

  /**
   * 判断主连接是否过期
   */
  isMasterExpired(masterData) {
    try {
      const data = JSON.parse(masterData);
      return Date.now() - data.timestamp > 10000; // 10秒未更新认为过期
    } catch {
      return true;
    }
  }

  /**
   * 生成标签页ID
   */
  generateTabId() {
    if (!sessionStorage.tabId) {
      sessionStorage.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return sessionStorage.tabId;
  }

  getTabId() {
    return sessionStorage.tabId || this.generateTabId();
  }

  /**
   * 设置消息处理回调
   */
  setMessageCallback(callback) {
    this.onMessageCallback = callback;
  }

  /**
   * 页面卸载清理
   */
  setupUnloadHandler() {
    window.addEventListener('beforeunload', () => {
      if (this.isMaster) {
        this.clearMasterClaim();
      }
      if (this.masterCheckInterval) {
        clearInterval(this.masterCheckInterval);
      }
      if (this.eventSource) {
        this.eventSource.close();
      }
      this.broadcastChannel.close();
    });
  }

  /**
   * 重启SSE连接
   */
  restartSSEConnection() {
    if (this.eventSource) {
      this.eventSource.close();
    }
    this.startSSEConnection();
  }

  /**
   * 手动关闭连接
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.masterCheckInterval) {
      clearInterval(this.masterCheckInterval);
      this.masterCheckInterval = null;
    }
    this.isMaster = false;
    this.clearMasterClaim();
  }
}